import {
  Badge,
  Button,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@devhop/ui";
import { BanIcon, MoreHorizontalIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getTodayYYYYMMDD } from "@/shared/lib/date-time";
import type { RehearsalSessionDTO } from "../../domain/contracts";
import {
  buildRehearsalScheduleMatrix,
  type RehearsalMatrixBulkAddPayload,
  type RehearsalMatrixCell,
  rehearsalMatrixBulkAddToPlanForDate,
  rehearsalMatrixBulkAddToPlanForPerformer,
  rehearsalMatrixPlannedSessionIdsForDate,
  rehearsalMatrixPlannedSessionIdsForPerformer,
  rehearsalMatrixSelectableSessionIds,
  rehearsalMatrixSessionKey,
} from "../../domain/services/rehearsal-schedule-matrix";
import {
  REHEARSAL_SESSION_STATUS,
  type RehearsalSessionStatusMeta,
} from "../../domain/services/rehearsal-sessions-ui";
import type { EventPerformerOption } from "./RehearsalSessionDialog";

type RehearsalScheduleMatrixProps = {
  eventPerformers: EventPerformerOption[];
  sessions: RehearsalSessionDTO[];
  canManage: boolean;
  submitting?: boolean;
  onAddToPlan?: (
    performerId: string,
    scheduledDate: string,
  ) => Promise<void>;
  onBulkStatusChange?: (
    sessionIds: string[],
    status: RehearsalSessionDTO["status"],
  ) => Promise<void>;
  onBulkAddToPlan?: (payload: RehearsalMatrixBulkAddPayload) => Promise<void>;
};

function splitRehearsalHeaderDate(label: string) {
  const [day = "", month = "", year = ""] = label.split("/");
  return { day, month, year };
}

function MatrixCell({
  cell,
  canManage,
  submitting,
  selected = false,
  onSelectedChange,
  onAddToPlan,
}: {
  cell: RehearsalMatrixCell;
  canManage: boolean;
  submitting: boolean;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  onAddToPlan?: () => void;
}) {
  if (cell.kind === "none") {
    const canAdd = canManage && onAddToPlan != null;

    if (canAdd) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex h-11 w-full items-center justify-center rounded-md border border-dashed bg-muted/20 text-muted-foreground/50 transition-colors",
                "hover:border-primary/40 hover:bg-muted/40 hover:text-muted-foreground",
                submitting && "cursor-wait opacity-60",
              )}
              disabled={submitting}
              onClick={onAddToPlan}
              aria-label="ເພີ່ມເຂົ້າແຜນ"
            >
              —
            </button>
          </TooltipTrigger>
          <TooltipContent>ກົດເພື່ອເພີ່ມເຂົ້າແຜນ</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div
        className="flex h-11 items-center justify-center rounded-md border border-dashed bg-muted/20 text-muted-foreground/50"
        aria-label="ບໍ່ມີໃນແຜນ"
      >
        —
      </div>
    );
  }

  const { session } = cell;
  const meta = REHEARSAL_SESSION_STATUS[session.status];
  const isCompleted = session.status === "completed";
  const isCancelled = session.status === "cancelled";

  if (isCancelled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex h-11 items-center justify-center rounded-md border border-destructive/30 bg-destructive/5 text-destructive">
            <BanIcon className="size-4" aria-hidden />
            <span className="sr-only">{meta.label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>{meta.label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <label
          className={cn(
            "flex h-11 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border px-1 transition-colors",
            isCompleted
              ? "border-primary/30 bg-primary/10 hover:bg-primary/15"
              : "border-border bg-card hover:bg-muted/40",
            selected && "ring-2 ring-primary/40 bg-primary/5",
            submitting && "opacity-70",
          )}
        >
          {canManage && onSelectedChange ? (
            <Checkbox
              checked={selected}
              disabled={submitting}
              onCheckedChange={(checked) => onSelectedChange(checked === true)}
              aria-label={`ເລືອກ ${session.performerName ?? session.performerId} — ${meta.label}`}
            />
          ) : (
            <span className={`size-2 rounded-full ${meta.dotClass}`} />
          )}
          <span className="max-w-full truncate text-[10px] text-muted-foreground leading-none">
            {meta.label.split(" (")[0]}
          </span>
        </label>
      </TooltipTrigger>
      <TooltipContent>{meta.label}</TooltipContent>
    </Tooltip>
  );
}

function MatrixBulkMenu({
  disabled,
  confirmCount,
  addCount,
  confirmLabel,
  addLabel,
  onConfirm,
  onAdd,
  align = "center",
}: {
  disabled: boolean;
  confirmCount: number;
  addCount: number;
  confirmLabel: string;
  addLabel: string;
  onConfirm?: () => void;
  onAdd?: () => void;
  align?: "start" | "center" | "end";
}) {
  if (confirmCount === 0 && addCount === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0 text-muted-foreground"
          disabled={disabled}
          aria-label="ຈັດການເປັນກຸ່ມ"
        >
          <MoreHorizontalIcon className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-44">
        {confirmCount > 0 && onConfirm ? (
          <DropdownMenuItem disabled={disabled} onClick={onConfirm}>
            {confirmLabel} ({confirmCount})
          </DropdownMenuItem>
        ) : null}
        {addCount > 0 && onAdd ? (
          <DropdownMenuItem disabled={disabled} onClick={onAdd}>
            {addLabel} ({addCount})
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function RehearsalScheduleMatrix({
  eventPerformers,
  sessions,
  canManage,
  submitting = false,
  onAddToPlan,
  onBulkStatusChange,
  onBulkAddToPlan,
}: RehearsalScheduleMatrixProps) {
  const today = useMemo(() => getTodayYYYYMMDD(), []);
  const [pendingCellKey, setPendingCellKey] = useState<string | null>(null);
  const [pendingBulkKey, setPendingBulkKey] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<RehearsalSessionDTO["status"]>(
    "completed",
  );

  const model = useMemo(
    () => buildRehearsalScheduleMatrix(eventPerformers, sessions),
    [eventPerformers, sessions],
  );

  const selectableSessionIds = useMemo(
    () => rehearsalMatrixSelectableSessionIds(model),
    [model],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const allowed = new Set(selectableSessionIds);
      const next = new Set<string>();
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
      }
      return next;
    });
  }, [selectableSessionIds]);

  const allSelected =
    selectableSessionIds.length > 0 &&
    selectableSessionIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const selectedCount = selectedIds.size;

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(selectableSessionIds) : new Set());
  };

  const toggleOne = (sessionId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(sessionId);
      else next.delete(sessionId);
      return next;
    });
  };

  const handleBulkUpdate = async () => {
    if (!onBulkStatusChange || selectedCount === 0) return;
    await onBulkStatusChange([...selectedIds], bulkStatus);
    setSelectedIds(new Set());
  };

  const handleAddToPlan = async (performerId: string, scheduledDate: string) => {
    if (!onAddToPlan) return;

    const cellKey = rehearsalMatrixSessionKey(performerId, scheduledDate);
    setPendingCellKey(cellKey);
    try {
      await onAddToPlan(performerId, scheduledDate);
    } finally {
      setPendingCellKey(null);
    }
  };

  const runBulkAction = async (bulkKey: string, action: () => Promise<void>) => {
    setPendingBulkKey(bulkKey);
    try {
      await action();
    } finally {
      setPendingBulkKey(null);
    }
  };

  const handleConfirmColumn = async (scheduledDate: string) => {
    if (!onBulkStatusChange) return;
    const sessionIds = rehearsalMatrixPlannedSessionIdsForDate(model, scheduledDate);
    if (sessionIds.length === 0) return;

    await runBulkAction(`col-confirm:${scheduledDate}`, async () => {
      await onBulkStatusChange(sessionIds, "completed");
    });
  };

  const handleConfirmRow = async (performerId: string) => {
    if (!onBulkStatusChange) return;
    const sessionIds = rehearsalMatrixPlannedSessionIdsForPerformer(
      model,
      performerId,
    );
    if (sessionIds.length === 0) return;

    await runBulkAction(`row-confirm:${performerId}`, async () => {
      await onBulkStatusChange(sessionIds, "completed");
    });
  };

  const handleAddColumn = async (scheduledDate: string) => {
    if (!onBulkAddToPlan) return;
    const payload = rehearsalMatrixBulkAddToPlanForDate(model, scheduledDate);
    if (!payload) return;

    await runBulkAction(`col-add:${scheduledDate}`, async () => {
      await onBulkAddToPlan(payload);
    });
  };

  const handleAddRow = async (performerId: string) => {
    if (!onBulkAddToPlan) return;
    const payload = rehearsalMatrixBulkAddToPlanForPerformer(model, performerId);
    if (!payload) return;

    await runBulkAction(`row-add:${performerId}`, async () => {
      await onBulkAddToPlan(payload);
    });
  };

  if (model.dates.length === 0) {
    return (
      <p className="px-5 py-8 text-center text-muted-foreground text-sm">
        {canManage
          ? "ຍັງບໍ່ມີວັນຊ້ອມ — ໃຊ້ປຸ່ມ «ເພີ່ມວັນຊ້ອມ» ຫຼື ສະຫຼັບໄປມຸມມອງລາຍການຕາມວັນ"
          : "ຍັງບໍ່ມີວັນຊ້ອມ"}
      </p>
    );
  }

  const toolbarBusy = submitting || pendingBulkKey != null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-3 px-4 pb-4">
        <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-xs">
          {(
            Object.values(REHEARSAL_SESSION_STATUS) as RehearsalSessionStatusMeta[]
          ).map((status) => (
            <span key={status.id} className="inline-flex items-center gap-1.5">
              <span className={`inline-block size-2 rounded-full ${status.dotClass}`} />
              {status.label.split(" (")[0]}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex h-5 w-8 items-center justify-center rounded border border-dashed bg-muted/20">
              —
            </span>
            ບໍ່ມີໃນແຜນ
          </span>
        </div>

        {canManage ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={
                  allSelected ? true : someSelected ? "indeterminate" : false
                }
                onCheckedChange={(checked) => toggleAll(checked === true)}
                disabled={toolbarBusy || selectableSessionIds.length === 0}
                aria-label="ເລືອກທັງໝົດ"
              />
              <span className="text-muted-foreground">
                {someSelected
                  ? `ເລືອກ ${selectedCount}/${selectableSessionIds.length}`
                  : "ເລືອກທັງໝົດ"}
              </span>
            </label>

            {someSelected ? (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Select
                  value={bulkStatus}
                  onValueChange={(value) =>
                    setBulkStatus(value as RehearsalSessionDTO["status"])
                  }
                  disabled={toolbarBusy}
                >
                  <SelectTrigger className="h-8 w-44">
                    <SelectValue placeholder="ເລືອກສະຖານະ" />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      Object.values(
                        REHEARSAL_SESSION_STATUS,
                      ) as RehearsalSessionStatusMeta[]
                    ).map((status) => (
                      <SelectItem key={status.id} value={status.id}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  disabled={toolbarBusy || !onBulkStatusChange}
                  onClick={() => void handleBulkUpdate()}
                >
                  ອັບເດດສະຖານະ ({selectedCount})
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="sticky left-0 z-20 min-w-[180px] border-r bg-muted/40 px-3 py-2 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ນັກສະແດງ
                </th>
                {model.dates.map((dateCol) => {
                  const { day, month, year } = splitRehearsalHeaderDate(
                    dateCol.label,
                  );
                  const isToday = dateCol.scheduledDate === today;
                  const plannedIds = rehearsalMatrixPlannedSessionIdsForDate(
                    model,
                    dateCol.scheduledDate,
                  );
                  const addPayload = rehearsalMatrixBulkAddToPlanForDate(
                    model,
                    dateCol.scheduledDate,
                  );
                  const bulkBusy =
                    toolbarBusy || pendingBulkKey?.startsWith("col-") === true;
                  return (
                    <th
                      key={dateCol.scheduledDate}
                      className="min-w-[88px] px-2 py-2 text-center align-top"
                    >
                      <div className="mx-auto flex w-[72px] flex-col items-center gap-1">
                        <div
                          className={cn(
                            "flex w-full flex-col items-center rounded-md border bg-card px-1 py-1 font-semibold tabular-nums leading-none",
                            isToday && "border-primary/40 bg-primary/5",
                          )}
                        >
                          <span className="text-base">{day}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {month}/{year}
                          </span>
                        </div>
                        {isToday ? (
                          <Badge variant="secondary" className="text-[10px]">
                            ມື້ນີ້
                          </Badge>
                        ) : null}
                        <span className="text-[10px] text-muted-foreground leading-tight">
                          {dateCol.statusCounts.completed}/
                          {dateCol.statusCounts.planned +
                            dateCol.statusCounts.completed +
                            dateCol.statusCounts.cancelled}{" "}
                          ສຳເລັດ
                        </span>
                        {canManage ? (
                          <MatrixBulkMenu
                            disabled={bulkBusy}
                            confirmCount={plannedIds.length}
                            addCount={addPayload?.performerIds.length ?? 0}
                            confirmLabel="ຢືນຢັນທຸກຄົນ"
                            addLabel="ເພີ່ມທຸກຄົນ"
                            onConfirm={
                              onBulkStatusChange
                                ? () => void handleConfirmColumn(dateCol.scheduledDate)
                                : undefined
                            }
                            onAdd={
                              onBulkAddToPlan
                                ? () => void handleAddColumn(dateCol.scheduledDate)
                                : undefined
                            }
                          />
                        ) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => {
                const plannedIds = rehearsalMatrixPlannedSessionIdsForPerformer(
                  model,
                  row.performerId,
                );
                const addPayload = rehearsalMatrixBulkAddToPlanForPerformer(
                  model,
                  row.performerId,
                );
                const rowBulkBusy =
                  toolbarBusy || pendingBulkKey?.startsWith("row-") === true;

                return (
                  <tr key={row.performerId} className="border-b last:border-b-0">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-r bg-card px-3 py-2 text-left font-medium"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="min-w-0 truncate">{row.performerName}</span>
                        {canManage ? (
                          <MatrixBulkMenu
                            disabled={rowBulkBusy}
                            confirmCount={plannedIds.length}
                            addCount={addPayload?.scheduledDates.length ?? 0}
                            confirmLabel="ຢືນຢັນທັງແຖວ"
                            addLabel="ເພີ່ມທັງແຖວ"
                            align="start"
                            onConfirm={
                              onBulkStatusChange
                                ? () => void handleConfirmRow(row.performerId)
                                : undefined
                            }
                            onAdd={
                              onBulkAddToPlan
                                ? () => void handleAddRow(row.performerId)
                                : undefined
                            }
                          />
                        ) : null}
                      </div>
                    </th>
                    {row.cells.map((cell, index) => {
                      const dateCol = model.dates[index];
                      if (!dateCol) return null;
                      const cellKey = rehearsalMatrixSessionKey(
                        row.performerId,
                        dateCol.scheduledDate,
                      );
                      const sessionId =
                        cell.kind === "session" ? cell.session.id : null;
                      const cellBusy =
                        toolbarBusy ||
                        pendingCellKey === cellKey;
                      return (
                        <td
                          key={`${row.performerId}-${dateCol.scheduledDate}`}
                          className="px-2 py-2 align-middle"
                        >
                          <MatrixCell
                            cell={cell}
                            canManage={canManage}
                            submitting={cellBusy}
                            selected={
                              sessionId != null && selectedIds.has(sessionId)
                            }
                            onSelectedChange={
                              sessionId && canManage
                                ? (checked) => toggleOne(sessionId, checked)
                                : undefined
                            }
                            onAddToPlan={
                              cell.kind === "none" && onAddToPlan
                                ? () =>
                                    void handleAddToPlan(
                                      row.performerId,
                                      dateCol.scheduledDate,
                                    )
                                : undefined
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {canManage ? (
          <p className="text-muted-foreground text-xs">
            ເລືອກ box ແລ້ວເລືອກສະຖານະ ແລ້ວກົດ «ອັບເດດສະຖານະ» — ກົດ — ເພື່ອເພີ່ມເຂົ້າແຜນ • ຈອງຫ້ອງ/ລາຍລະອຽດໃຊ້ «ຕາມວັນ»
          </p>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
